import {Button, Typography} from '@mui/material';
import {t} from 'i18next';
import {startTransition, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {ReactComponent as DropDownIcon} from '../../../../../src/assests/svg/down_arrow_custom.svg';
import {getIndexByClientIdAndProjectId} from '../../../../api/EcaServices';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {RootState} from '../../../../redux/store';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {AurSelect} from '../../../common/aur-select/AurSelect';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import ECASavedSearchPanelSideDrawer, {
  SavedSearchType,
} from '../../../eca/ECASavedSearchSidePanelDrawer';
import {validateAllowedSpecialCharacters} from '../../../../utils/utils';
import AurButton from '../../../common/aur-button/AurButton';
import {showErrorToast} from '../../../../api/utils';
import {Flow_Type} from '../../../eca/SavedSearchPanel';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
interface ScopeContentProps {
  initialData: ScopeContentType | null;
  onDataChange: (data: ScopeContentType) => void;
  isEditFlow: boolean;
}

interface IndexData {
  name: string;
  type: string;
  fieldTemplateId: number;
}

export type ScopeContentType = {
  reportName: string;
  customNote: string;
  selectedIndex: string;
  selectedSearch: SavedSearchType | null;
  fieldTemplateId: number;
  isValid: boolean;
};

export const MAX_CHARACTER_COUNT = 50;
const INDEX_TYPE_STANDARD = 'Standard';
const NLP_INDEX_TYPE = 'Nlp';

const ScopeContent = (props: ScopeContentProps) => {
  const {initialData, onDataChange, isEditFlow = false} = props;
  const stringPath = 'eca.searchTermReport.scopeContent.';
  const [indexList, setIndexList] = useState<IndexData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState('');
  const [reportName, setReportName] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isSaveSearchDialogVisible, setIsSaveSearchDialogVisible] =
    useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] =
    useState<SavedSearchType | null>(null);
  const [isGetSearchIndexLoading, setIsGetSearchIndexLoading] =
    useState<boolean>(false);
  const [selectedFieldTemplateId, setSelectedFieldTemplateId] =
    useState<number>(1);

  const {showToast} = useToast();
  const {trackGAEvent} = useAnalytics();

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {projectId} = useSelector((state: RootState) => state.reports);

  const getIndexList = async (clientId: string, projectId: string) => {
    try {
      setIsGetSearchIndexLoading(true);
      const response = await getIndexByClientIdAndProjectId(
        clientId,
        projectId
      );
      if (response?.status == 200) {
        if (response?.data?.data?.length) {
          const indexes: IndexData[] = (response.data.data ?? []).filter(
            (item: IndexData) =>
              item.type.toLowerCase() !== NLP_INDEX_TYPE.toLowerCase()
          );
          setIndexList(indexes);
          const standeredIndex = indexes.find(
            (i: IndexData) => i.type === INDEX_TYPE_STANDARD
          );
          if (standeredIndex)
            continueIndexListOperation(standeredIndex, indexes);
        }
      }
    } catch (error: unknown) {
      showErrorToast(showToast, error, 'center');
    } finally {
      setIsGetSearchIndexLoading(false);
    }
  };

  const continueIndexListOperation = (
    standeredIndex: IndexData,
    response: IndexData[]
  ) => {
    if (initialData?.selectedIndex) {
      setSelectedIndex(initialData.selectedIndex);
    } else {
      setSelectedIndex(standeredIndex ? standeredIndex.name : response[0].name);
    }
    if (initialData?.fieldTemplateId) {
      setSelectedFieldTemplateId(initialData.fieldTemplateId);
    } else {
      setSelectedFieldTemplateId(
        standeredIndex
          ? standeredIndex.fieldTemplateId
          : response[0].fieldTemplateId
      );
    }
  };

  const indexMenuList = useMemo(() => {
    if (!selectedIndex)
      return indexList.map((i) => i.name).sort((a, b) => a.localeCompare(b));

    const otherIndices = indexList
      .map((i) => i.name)
      .filter((name) => name !== selectedIndex)
      .sort((a, b) => a.localeCompare(b, undefined, {numeric: true}));

    return [selectedIndex, ...otherIndices];
  }, [indexList, selectedIndex]);

  useEffect(() => {
    if (!!projectId && clientId) {
      getIndexList(clientId, projectId);
    } else {
      showToast(
        t('eca.searchTermReport.errorMessage'),
        'error',
        undefined,
        'center'
      );
    }
  }, [projectId, clientId]);

  useEffect(() => {
    if (initialData) {
      const {
        reportName,
        customNote,
        selectedIndex,
        selectedSearch,
        fieldTemplateId,
      } = initialData;
      startTransition(() => {
        setReportName(reportName);
        setCustomNote(customNote);
        setSelectedIndex(selectedIndex);
        setSelectedSearchIndex(selectedSearch);
        setSelectedFieldTemplateId(fieldTemplateId);
      });
    }
  }, []);

  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        reportName: reportName,
        customNote: customNote,
        selectedIndex: selectedIndex,
        selectedSearch: selectedSearchIndex,
        fieldTemplateId: selectedFieldTemplateId,
        isValid: isValid,
      });
    }
  }, [
    selectedIndex,
    reportName,
    customNote,
    selectedSearchIndex,
    selectedFieldTemplateId,
    isValid,
  ]);

  useEffect(() => {
    if (indexList) {
      setSelectedIndex(
        indexList?.find(
          (i: IndexData) => i.fieldTemplateId == selectedFieldTemplateId
        )?.name ?? ''
      );
    }
  }, [indexList]);

  const handleSavedSearchSelection = (value: SavedSearchType) => {
    setSelectedSearchIndex(value);
    setIsSaveSearchDialogVisible(false);
  };

  // Prevent entering spaces
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' && reportName.endsWith(' ')) {
      // Prevent continuous spaces from being entered
      event.preventDefault();
    }
  };

  const reportNameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setReportName(newText);
    if (
      newText.length <= MAX_CHARACTER_COUNT &&
      validateAllowedSpecialCharacters(newText) &&
      !newText.includes(' ')
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const reportNameHelperText = useMemo(() => {
    const baseText = t(stringPath + 'field1.helperText3');

    if (
      reportName.length === 0 ||
      !isValid ||
      reportName.length > MAX_CHARACTER_COUNT
    ) {
      return baseText + '\n' + t(stringPath + 'field1.helperText1');
    }
    return (
      baseText +
      '\n' +
      t(stringPath + 'field1.helperText2') +
      `${MAX_CHARACTER_COUNT - reportName.length}`
    );
  }, [reportName, isValid]);
  const onIndexChange = (indexName: string) => {
    setSelectedIndex(indexName);
    const selectedIndex = indexList.find((i) => i.name === indexName);
    const indexFieldTemplateId = selectedIndex?.fieldTemplateId;
    setSelectedFieldTemplateId(indexFieldTemplateId ?? 0);
    const gaLabel =
      selectedIndex?.type === INDEX_TYPE_STANDARD
        ? 'standard index'
        : 'custom index';
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: gaLabel,
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.str.indexAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.steps.scope'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };
  return (
    <div className="str-scope-content-main">
      <Typography className="typo-text headline3_size">
        {t(stringPath + 'heading')}
      </Typography>
      <form className="str-scope-content-form">
        <div className="str-scope-content-form-left">
          <div className="str-scope-content-form-field">
            <Typography className="typo-text subhead4_size" id="report-name-label">
              {t(stringPath + 'field1.label1')}
            </Typography>
            <AurTextField
              helperText={reportNameHelperText}
              className="aur-textfield custom-text-field str-text-field"
              variant="filled"
              placeholder={t(stringPath + 'field1.placeholderText')}
              value={reportName}
              onKeyDown={handleKeyDown} // Listen for the key down event
              onChange={reportNameChangeHandler}
              error={!isValid}
              data-testid="reportTextField"
              disabled={isEditFlow}
              ariaLabel={t(stringPath + 'field1.label1')}
              inputProps={{
                'aria-labelledby': 'report-name-label',
                maxLength: MAX_CHARACTER_COUNT, // Limit input length to 50 characters
              }}
              FormHelperTextProps={{sx: {whiteSpace: 'pre-line'}}}
            />
          </div>
          <div className="str-scope-content-form-field mid-field">
            <Typography
              id="search-index-label"
              className="typo-text subhead4_size"
            >
              {t(stringPath + 'field2.label')}
            </Typography>
            <AurSelect
              label=""
              labelId="search-index-label"
              name={t(stringPath + 'field2.label')}
              arialabel={t(stringPath + 'field2.hintText')}
              optionList={indexMenuList}
              field={{
                value: selectedIndex,
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onChange: () => {},
              }}
              fieldState={{}}
              onSelect={(event) => {
                onIndexChange(event.target.value);
              }}
              disabled={indexList.length === 1}
              dataTestId="selectSearchIndex"
            />
          </div>

          <div className="str-scope-content-form-field">
            <div>
              <Typography className="typo-text subhead4_size">
                {t(stringPath + 'field3.label')}
              </Typography>

              <Typography className="typo-text body2_size saved-search-title-guide-label">
                {t(stringPath + 'field3.label2')}
              </Typography>
            </div>

            <div className="pick-saved-search-container-row">
              <Button
                variant="outlined"
                name={t(stringPath + 'field3.saveSearch')}
                endIcon={<DropDownIcon data-testid="drop-down-arrow-down" aria-hidden="true" />}
                className="select-saved-search-button"
                sx={{textTransform: 'none'}}
                aria-describedby={t(stringPath + 'field3.buttonHint')}
                onClick={() => {
                  setIsSaveSearchDialogVisible(true);
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.CLICK,
                    click: {
                      name: t('gaTracking.str.selecteSavedSearch'),
                      type: GA_CLICK_TYPES.BUTTON,
                      region: GA_REGIONS.TAB,
                      action: t('gaTracking.str.savedSearchAction'),
                    },
                    subPage: {
                      subpage_name: t('gaTracking.str.steps.scope'),
                      subpage_type: GA_SUBPAGE_TYPE.TAB,
                    },
                  });
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                    subPage: {
                      subpage_name: t('gaTracking.str.selecteSavedSearch'),
                      subpage_type: GA_SUBPAGE_TYPE.DRAWER,
                    },
                  });
                }}
              >
                <Typography className="typo-text body2_size select-button-text">
                  {!selectedSearchIndex?.label
                    ? t(stringPath + 'field3.buttonLabel')
                    : selectedSearchIndex?.label}
                </Typography>
              </Button>
              {selectedSearchIndex ? (
                <AurButton
                  type="text"
                  label={t('common.clear')}
                  title={t('common.clear')}
                  size="Large"
                  onClick={setSelectedSearchIndex.bind(this, null)}
                  dataTestId="clear-picked-search-button"
                  ariaLabel={t('queryWizard.clearSavedSearchSelection')}
                />
              ) : null}
            </div>
          </div>
        </div>
        <div className="str-scope-content-form-right">
          <Typography className="typo-text subhead4_size">
            {indexList.length === 1
              ? t(stringPath + 'field4.label2')
              : t(stringPath + 'field4.label1')}
            <span className="optional"> {t(stringPath + 'optional')}</span>
          </Typography>
          <AurTextField
            className="aur-textfield custom-text-field str-note-field"
            variant="filled"
            multiline
            minRows={3}
            maxRows={14}
            placeholder={t(stringPath + 'field4.placeholderText')}
            value={customNote}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCustomNote(e.target.value)
            }
            data-testid="addNoteTextField"
            ariaLabel={indexList.length === 1
              ? t(stringPath + 'field4.label2')
              : t(stringPath + 'field4.label1')}
          />
        </div>
      </form>
      <ECASavedSearchPanelSideDrawer
        showDrawer={isSaveSearchDialogVisible}
        onCloseDrawer={() => {
          setIsSaveSearchDialogVisible(false);
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('gaTracking.global.cancel'),
              type: GA_CLICK_TYPES.BUTTON,
              region: GA_REGIONS.DRAWER,
              action: t('gaTracking.str.savedSearchCancelAction'),
            },
            subPage: {
              subpage_name: t('gaTracking.str.selecteSavedSearch'),
              subpage_type: GA_SUBPAGE_TYPE.DRAWER,
            },
          });
        }}
        selectedIndex={selectedIndex}
        onSelectSavedSearch={handleSavedSearchSelection}
        flowType={Flow_Type.STR}
        triggerResetPickedSearch={!selectedSearchIndex}
      />
      <AurLoadingIndicator overlay={true} isOpen={isGetSearchIndexLoading} />
    </div>
  );
};

export default ScopeContent;
